import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Platform, Linking } from 'react-native';
import * as Location from 'expo-location';
import { useAuth } from './AuthProvider';
import { usePermissionsTick } from './PermissionsBootstrap';
import { AccountService } from '@/services/domain/AccountService';
import { ApiService } from '@/services/ApiService';
import StorageService from '@/services/StorageService';
import { useAppState } from '@/hooks/useAppState';
import { LocationPayload } from '~/services/domain/AccountTypes';

/**
 * Detects and repairs UTF-8 → Latin-1 mojibake (e.g. "MontrÃ©al" → "Montréal").
 * Reinterprets each char code as a raw byte and re-decodes as UTF-8.
 * Returns the original string unchanged when no mojibake is detected.
 */
const fixMojibake = (str: string): string => {
  if (!/[\xC0-\xFF]/.test(str)) return str;

  try {
    const bytes = new Uint8Array(str.length);
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i);
      if (code > 255) return str;
      bytes[i] = code;
    }
    const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    if (decoded.length < str.length) return decoded;
  } catch {}

  return str;
};

// Location configuration constants
const LOCATION_UPDATE_MIN_INTERVAL_MS = 300_000; // At most one update per 5 minutes
const LOCATION_SKIP_ACCURACY_THRESHOLD_M = 5000; // Skip posting when accuracy worse than this
const LOCATION_MIN_DISTANCE_THRESHOLD_M = 100; // Minimum distance (plus accuracy) before posting update
const EARTH_RADIUS_M = 6_371_000; // Haversine earth radius
// Substantial movement required (plus accuracy) before updating place name on web+auto
const PLACE_NAME_ANCHOR_DISTANCE_M = 1000;

// Storage keys
const STORAGE_KEYS = {
  MODE: 'placeUpdateMode',
  ACCURACY: 'placeLastAccuracy',
  LAST_UPDATE: 'placeLastUpdateAt',
  // Stores last lat/lon where updatePlaceName was true (web+auto gating)
  PLACE_ANCHOR: 'placeLastPlaceNameAnchor',
} as const;

// Platform-specific location accuracy settings
const getLocationAccuracy = () => ({
  accuracy: Platform.select({
    android: Location.Accuracy.Balanced,
    web: Location.Accuracy.High,
    default: Location.Accuracy.High,
  }),
  mayShowUserSettingsDialog: Platform.OS === 'ios',
});

/**
 * Determines if the location accuracy is good enough for updates.
 * Null accuracy is considered acceptable.
 */
const hasGoodAccuracy = (accuracy: number | null): boolean => {
  return accuracy === null || accuracy <= LOCATION_SKIP_ACCURACY_THRESHOLD_M;
};

type PlaceMode = 'auto' | 'fixed' | null;

interface LocationContextValue {
  placeMode: PlaceMode;
  setPlaceMode: (mode: Exclude<PlaceMode, null>) => Promise<void>;
  lastAccuracy: number | null;
  hasGoodAccuracy: boolean;
  subscribePlaceNameUpdates: (handler: (placeName: string) => void) => () => void;
  hasLocationPermission: boolean;
  requestLocationPermission: () => Promise<boolean>;
  canAskLocationPermissionAgain: boolean;
  openLocationSettings: () => Promise<void>;
  setFixedPlace: (place: PlaceSearchResult) => Promise<void>;
}

export type PlaceSearchResult = {
  id?: string;
  displayName: string;
  latitude: number;
  longitude: number;
  countryCode?: string;
};

const LocationContext = createContext<LocationContextValue | undefined>(undefined);
export const useLocation = (): LocationContextValue => {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocation must be used within LocationProvider');
  return ctx;
};

// Helper function to safely execute async operations
const safeAsync = async <T,>(fn: () => Promise<T>, fallback?: T): Promise<T | undefined> => {
  try {
    return await fn();
  } catch {
    return fallback;
  }
};

// Helper to update permission state from response
const updatePermissionState = (
  resp: Location.LocationPermissionResponse,
  setHasPermission: (value: boolean) => void,
  setCanAskAgain: (value: boolean) => void
) => {
  setHasPermission(resp.status === Location.PermissionStatus.GRANTED);
  setCanAskAgain(Boolean((resp as any).canAskAgain ?? resp.canAskAgain));
};

/**
 * Tracks foreground location when authenticated and reports to backend.
 * - Requests when-in-use permission on first need
 * - Sends initial fix on app start/sign-in
 * - Watches for movements >250m and reports
 * - Cleans up watcher on sign-out/unauthenticated
 */
export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const permissionsTick = usePermissionsTick();
  const lastReportedRef = useRef<{ latitude: number; longitude: number } | null>(null);
  const lastUpdateAtRef = useRef<number>(0);
  // Prevents concurrent maybeUpdateLocation runs (e.g. permission grant +
  // AppState onForeground bouncing during the dialog) from each passing
  // isRateLimited and posting duplicate updates before the first finishes.
  const updateInFlightRef = useRef<boolean>(false);
  const modeRef = useRef<PlaceMode>(null);
  const lastAccuracyRef = useRef<number | null>(null);
  const [placeModeState, setPlaceModeState] = useState<PlaceMode>(null);
  const [lastAccuracyState, setLastAccuracyState] = useState<number | null>(null);
  const [hasLocationPermission, setHasLocationPermission] = useState<boolean>(false);
  const [canAskLocationPermissionAgain, setCanAskLocationPermissionAgain] = useState<boolean>(true);
  const placeNameListenersRef = useRef<Set<(placeName: string) => void>>(new Set());

  const getMode = async (): Promise<PlaceMode> => {
    const v = await StorageService.getItem(STORAGE_KEYS.MODE);
    return v === 'auto' || v === 'fixed' ? v : null;
  };

  const forceLocationUpdateIfPossible = async () => {
    if (!isAuthenticated) return;
    const resp = await safeAsync(() => Location.getForegroundPermissionsAsync());
    if (resp) updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);
    if (resp?.status !== Location.PermissionStatus.GRANTED) return;
    
    const current = await safeAsync(() => Location.getCurrentPositionAsync(getLocationAccuracy()));
    if (current) {
      if (__DEV__) console.log('[LocationProvider] Forcing immediate update due to mode change to auto');
      await sendLocation(current.coords);
      await setLastUpdateAt(Date.now());
    }
  };

  const setMode = async (value: Exclude<PlaceMode, null>) => {
    const previous = modeRef.current;
    modeRef.current = value;
    setPlaceModeState(value);
    await StorageService.setItem(STORAGE_KEYS.MODE, value);

    if (previous !== 'auto' && value === 'auto') {
      await forceLocationUpdateIfPossible();
    }

    // When switching from auto to fixed, clear the place-name anchor
    if (previous === 'auto' && value === 'fixed' && Platform.OS === 'web') {
      await StorageService.removeItem(STORAGE_KEYS.PLACE_ANCHOR);
    }
  };

  const setLastAccuracy = async (accuracy: number | null) => {
    lastAccuracyRef.current = accuracy;
    setLastAccuracyState(accuracy);
    if (accuracy !== null && accuracy !== undefined && !Number.isNaN(accuracy)) {
      await StorageService.setItem(STORAGE_KEYS.ACCURACY, String(accuracy));
    }
  };

  const setLastUpdateAt = async (timestampMs: number) => {
    lastUpdateAtRef.current = timestampMs;
    if (Platform.OS === 'web') {
      await safeAsync(() => StorageService.setItem(STORAGE_KEYS.LAST_UPDATE, String(timestampMs)));
    }
  };

  useEffect(() => {
    const init = async () => {
      // Initialize permission snapshot
      const resp = await safeAsync(() => Location.getForegroundPermissionsAsync());
      if (resp) updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);

      // Initialize mode
      const storedMode = await getMode();
      modeRef.current = storedMode;
      setPlaceModeState(storedMode);
      
      // Initialize accuracy
      const accStr = await StorageService.getItem(STORAGE_KEYS.ACCURACY);
      const accNum = accStr ? Number(accStr) : null;
      lastAccuracyRef.current = accNum;
      setLastAccuracyState(accNum);

      // Restore last update timestamp (web only)
      if (Platform.OS === 'web') {
        const tsStr = await StorageService.getItem(STORAGE_KEYS.LAST_UPDATE);
        const tsNum = tsStr ? Number(tsStr) : 0;
        if (!Number.isNaN(tsNum) && tsNum > 0) {
          lastUpdateAtRef.current = tsNum;
        }
      }
    };
    init();
  }, []);

  // Haversine distance in meters
  const distanceBetween = (
    a: { latitude: number; longitude: number },
    b: { latitude: number; longitude: number }
  ): number => {
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = EARTH_RADIUS_M;
    const dLat = toRad(b.latitude - a.latitude);
    const dLon = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    return 2 * R * Math.asin(Math.sqrt(h));
  };

  // Helper for reverse geocoding
  const getReverseGeocode = async (coords: { latitude: number; longitude: number }) => {
    if (Platform.OS === 'web') return {};
    
    const results = await safeAsync(() => Location.reverseGeocodeAsync(coords));
    const addr = results?.[0];
    if (!addr) return {};

    const locationParts = [
      (addr as any).city || (addr as any).subregion || (addr as any).district || (addr as any).name,
      (addr as any).region,
      (addr as any).country,
    ].filter(Boolean).map(fixMojibake);

    return {
      placeName: locationParts.length ? locationParts.join(', ') : undefined,
      countryCode: (addr as any).isoCountryCode?.toUpperCase(),
    };
  };

  const notifyPlaceNameListeners = (placeName: string) => {
    placeNameListenersRef.current.forEach((fn) => {
      try {
        fn(placeName);
      } catch {}
    });
  };

  const sendLocation = async (coords: Location.LocationObjectCoords) => {
    const accuracy = coords.accuracy ?? null;
    await setLastAccuracy(accuracy);

    // Initialize mode if unset
    if (!modeRef.current) {
      const existing = await getMode();
      modeRef.current = existing || (hasGoodAccuracy(accuracy) ? 'auto' : 'fixed');
      if (!existing) await setMode(modeRef.current);
    }

    // Skip if accuracy too low
    if (!hasGoodAccuracy(accuracy)) {
      if (__DEV__) console.log('[LocationProvider] Skipping update due to low accuracy:', accuracy);
      return;
    }

    const { placeName, countryCode } = await getReverseGeocode(coords);
    const payload: LocationPayload = {
      Latitude: coords.latitude,
      Longitude: coords.longitude,
      Accuracy: coords.accuracy ?? undefined,
      PlaceName: placeName,
      CountryCode: countryCode,
    };

    let shouldUpdatePlaceName = modeRef.current !== 'fixed';

    // Gate place-name updates on web+auto: require substantial movement from last anchor
    // This is to prevent the backend from doing reverse geocoding for every update
    // Threshold = PLACE_NAME_ANCHOR_DISTANCE_M + current accuracy
    if (shouldUpdatePlaceName && Platform.OS === 'web' && modeRef.current === 'auto') {
      try {
        const anchorRaw = await StorageService.getItem(STORAGE_KEYS.PLACE_ANCHOR);
        if (anchorRaw) {
          const anchor = JSON.parse(anchorRaw) as { latitude: number; longitude: number } | null;
          if (anchor && typeof anchor.latitude === 'number' && typeof anchor.longitude === 'number') {
            const distance = distanceBetween(anchor, coords);
            const currentAccuracy = coords.accuracy ?? 0;
            const threshold = PLACE_NAME_ANCHOR_DISTANCE_M + currentAccuracy;
            if (distance < threshold) {
              if (__DEV__) {
                console.log('[LocationProvider] Skipping place name update due to minimal movement from anchor (web+auto gating)');
              }
              shouldUpdatePlaceName = false;
            }
          }
        }
      } catch {}
    }
    if (__DEV__) {
      console.log('[LocationProvider] Posting update', shouldUpdatePlaceName ? 'with' : 'without', 'place name', payload);
    }

    // Sentry GROKIO-APP-70: sign-out can clear the community while GPS acquisition is in flight
    if (!ApiService.hasCommunity) {
      if (__DEV__) console.log('[LocationProvider] Skipping location update — no community selected (sign-out race)');
      return;
    }
    const updated = await AccountService.setLocation(payload, shouldUpdatePlaceName);

    // If we updated place name (web+auto gating or otherwise), persist a new anchor
    if (shouldUpdatePlaceName && Platform.OS === 'web') {
      await StorageService.setItem(
        STORAGE_KEYS.PLACE_ANCHOR,
        JSON.stringify({ latitude: coords.latitude, longitude: coords.longitude })
      );
    }
    const effectivePlaceName = shouldUpdatePlaceName
      ? updated?.PlaceName || placeName
      : undefined;
    
    if (effectivePlaceName) notifyPlaceNameListeners(effectivePlaceName);
    
    lastReportedRef.current = { latitude: coords.latitude, longitude: coords.longitude };
    await setLastUpdateAt(Date.now());
  };

  const isRateLimited = () => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateAtRef.current;
    if (lastUpdateAtRef.current && timeSinceLastUpdate < LOCATION_UPDATE_MIN_INTERVAL_MS) {
      if (__DEV__) {
        const remainingSec = Math.ceil((LOCATION_UPDATE_MIN_INTERVAL_MS - timeSinceLastUpdate) / 1000);
        console.log('[LocationProvider] Skipping update due to rate limit. Next allowed in', `${remainingSec}s`);
      }
      return true;
    }
    return false;
  };

  const isMovementTooSmall = (coords: Location.LocationObjectCoords) => {
    if (!lastReportedRef.current) return false;
    
    const distance = distanceBetween(lastReportedRef.current, coords);
    const accuracy = coords.accuracy ?? 0;
    const threshold = LOCATION_MIN_DISTANCE_THRESHOLD_M + accuracy;
    
    if (distance < threshold) {
      if (__DEV__) {
        console.log('[LocationProvider] Skipping update due to minimal movement:', {
          distance: Math.round(distance),
          threshold: Math.round(threshold),
          accuracy: coords.accuracy ?? null,
        });
      }
      return true;
    }
    return false;
  };

  const maybeUpdateLocation = async () => {
    if (!isAuthenticated || isRateLimited() || updateInFlightRef.current) return;
    updateInFlightRef.current = true;
    try {
      // Snapshot current permission
      const resp = await safeAsync(() => Location.getForegroundPermissionsAsync());
      if (resp) updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);

      const current = await safeAsync(() => Location.getCurrentPositionAsync(getLocationAccuracy()));
      if (current) {
        setHasLocationPermission(true);

        // Check for minimal movement before sending
        if (!isMovementTooSmall(current.coords)) {
          await sendLocation(current.coords);
        }
      } else if (Platform.OS !== 'web' && resp?.status !== Location.PermissionStatus.GRANTED) {
        // Only bail on non-web platforms with explicit denial
        return;
      }
    } finally {
      updateInFlightRef.current = false;
    }
  };

  // Observation-only snapshot: the actual post-login request flow is owned by
  // PermissionsBootstrap. This just keeps the provider's React state in sync
  // with whatever the OS currently reports.
  const refreshPermissionSnapshot = async () => {
    const resp = await safeAsync(() => Location.getForegroundPermissionsAsync());
    if (resp) updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);
  };

  useEffect(() => {
    if (isAuthenticated) {
      refreshPermissionSnapshot().then(() => {
        // One-shot update; no-op if permission isn't granted yet. The retry
        // path is `permissionsTick` from PermissionsBootstrap (works on iOS,
        // where the system permission dialog doesn't take the app through a
        // useful AppState transition) plus the `useAppState` hook below
        // (Android safety net via background→active).
        maybeUpdateLocation();
      });
    } else {
      lastReportedRef.current = null;
      lastUpdateAtRef.current = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, permissionsTick]);

  const refreshPermissionStateOnForeground = async (): Promise<void> => {
    const resp = await safeAsync(() => Location.getForegroundPermissionsAsync());
    if (!resp) return;

    if (Platform.OS === 'web') {
      // Safari workaround: verify permission with actual position request
      if (resp.status === Location.PermissionStatus.GRANTED) {
        setHasLocationPermission(true);
      } else {
        const probe = await safeAsync(() => Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Low }));
        if (probe) setHasLocationPermission(true);
      }
      if (typeof (resp as any).canAskAgain !== 'undefined') {
        setCanAskLocationPermissionAgain(Boolean((resp as any).canAskAgain));
      }
    } else {
      updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);
    }
  };

  useAppState({
    onForeground: () => {
      if (isAuthenticated) {
        // Trigger a rate-limited one-shot update on foreground
        maybeUpdateLocation();
      }
      // Refresh permission state on foreground (Safari-safe)
      refreshPermissionStateOnForeground();
    },
    onBackground: () => {
      // No background location; nothing to stop
    },
    //debugTag: 'Location',
  });

  const requestLocationPermission = async (): Promise<boolean> => {
    if (Platform.OS === 'web') {
      // Safari: trigger native prompt via direct geolocation request
      const current = await safeAsync(() => 
        Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.High })
      );
      if (current) {
        setHasLocationPermission(true);
        await sendLocation(current.coords);
        return true;
      } else {
        // Re-check permission state on failure
        const snap = await safeAsync(() => Location.getForegroundPermissionsAsync());
        if (snap) updatePermissionState(snap, setHasLocationPermission, setCanAskLocationPermissionAgain);
        return false;
      }
    }

    // User-gesture path (e.g. an in-app "enable location" button). Single,
    // explicit call — no coordinator needed because there is no concurrent
    // permission request happening at this point.
    const resp = await safeAsync(() => Location.requestForegroundPermissionsAsync());
    if (!resp) {
      setHasLocationPermission(false);
      setCanAskLocationPermissionAgain(false);
      return false;
    }

    updatePermissionState(resp, setHasLocationPermission, setCanAskLocationPermissionAgain);
    if (resp.status === Location.PermissionStatus.GRANTED) {
      await maybeUpdateLocation();
    }
    return resp.status === Location.PermissionStatus.GRANTED;
  };

  const openLocationSettings = () => safeAsync(() => Linking.openSettings());

  const setFixedPlace = async (place: PlaceSearchResult): Promise<void> => {
    const payload = {
      Latitude: place.latitude,
      Longitude: place.longitude,
      PlaceName: place.displayName,
      CountryCode: place.countryCode,
    };
    await safeAsync(() => AccountService.setLocation(payload, true));
    await setMode('fixed');
    notifyPlaceNameListeners(place.displayName);
  };

  return (
    <LocationContext.Provider
      value={{
        placeMode: placeModeState,
        setPlaceMode: setMode,
        lastAccuracy: lastAccuracyState,
        hasGoodAccuracy: hasGoodAccuracy(lastAccuracyState),
        hasLocationPermission,
        requestLocationPermission,
        canAskLocationPermissionAgain,
        openLocationSettings,
        subscribePlaceNameUpdates: (handler: (placeName: string) => void) => {
          placeNameListenersRef.current.add(handler);
          return () => {
            placeNameListenersRef.current.delete(handler);
          };
        },
        setFixedPlace,
      }}>
      {children}
    </LocationContext.Provider>
  );
};
